import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { LifecycleTemplateView, EmployeeSelectionView, ExitReasonView } from '../../../../models/employee';
import { getOffboardingEmployeeSelections } from '../../../../api/Employee/offboardingProcesses';
import { startOffboarding } from '../../../../api/Employee/offboardingProcesses';
import { getExitReasons } from '../../../../api/Employee/lifecycleSettings';
import { getUserManagers } from '../../../../api/General/account';

interface StartOffboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  templates: LifecycleTemplateView[];
}

const StartOffboardingModal: React.FC<StartOffboardingModalProps> = ({ isOpen, onClose, onSubmit, templates }) => {
  const { t } = useTranslation();
  const currentUserId = useSelector((state: any) => state.user.userId);
  const [employees, setEmployees] = useState<EmployeeSelectionView[]>([]);
  const [exitReasons, setExitReasons] = useState<ExitReasonView[]>([]);
  const [managers, setManagers] = useState<{ id: string; fullName: string }[]>([]);
  const [noSupervisor, setNoSupervisor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    userId: '',
    templateId: '',
    lastWorkingDay: '',
    exitReasonId: '',
    managerId: '',
  });

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSelectionView | null>(null);

  useEffect(() => {
    if (isOpen) {
      getOffboardingEmployeeSelections().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setEmployees(result.payload || []);
        }
      });
      getExitReasons().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setExitReasons((result.payload || []).filter(r => r.isActive));
        }
      });
      // Reset form
      // Never preselect a template — an offboarding template is optional (empty = terminate without a checklist).
      setFormData({ userId: '', templateId: '', lastWorkingDay: '', exitReasonId: '', managerId: '' });
      setSelectedEmployee(null);
      setManagers([]);
      setNoSupervisor(false);
      setError(null);
    }
  }, [isOpen, templates]);

  const handleEmployeeSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = e.target.value;
    const emp = employees.find(emp => emp.id === userId);

    // Reset manager state
    setFormData(prev => ({ ...prev, managerId: '' }));
    setNoSupervisor(false);
    setManagers([]);

    if (emp) {
      setSelectedEmployee(emp);
      setFormData(prev => ({ ...prev, userId: emp.id, managerId: '' }));

      // Fetch supervisors
      try {
        const result = await getUserManagers(userId);
        if (result.errors?.length === 0 && result.payload && result.payload.length > 0) {
          const supervisorList = result.payload.map((m: any) => ({
            id: m.id,
            fullName: m.fullName || `${m.firstName} ${m.lastName}`
          }));
          setManagers(supervisorList);
          // Auto-select if only one supervisor
          if (supervisorList.length === 1) {
            setFormData(prev => ({ ...prev, managerId: supervisorList[0].id }));
          }
        } else {
          setNoSupervisor(true);
          // Fallback: show all employees except the selected one
          setManagers(employees.filter(e => e.id !== userId).map(e => ({
            id: e.id,
            fullName: e.fullName
          })));
        }
      } catch {
        setNoSupervisor(true);
        setManagers(employees.filter(e => e.id !== userId).map(e => ({
          id: e.id,
          fullName: e.fullName
        })));
      }
    } else {
      setSelectedEmployee(null);
      setFormData(prev => ({ ...prev, userId: '' }));
    }
  };

  // Manager is selected from supervisor list
  const handleManagerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, managerId: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await startOffboarding({
        userId: formData.userId,
        templateId: formData.templateId ? parseInt(formData.templateId) : undefined,
        lastWorkingDay: formData.lastWorkingDay,
        exitReasonId: parseInt(formData.exitReasonId),
        managerId: formData.managerId,
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || 'Failed to start offboarding');
      } else {
        onSubmit();
      }
    } catch {
      setError('Failed to start offboarding');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Start Offboarding Process')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('Initiate employee exit process')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Select Employee')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.userId}
              onChange={handleEmployeeSelect}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Choose an employee...')}</option>
              {employees.filter(emp => emp.id !== currentUserId).map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName} - {emp.title || ''} ({emp.department || ''})
                </option>
              ))}
            </select>
          </div>

          {/* Employee Details (Auto-filled) */}
          {selectedEmployee && (
            <div className="bg-muted border border-border rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('Position')}</span>
                <div className="text-sm font-medium text-gray-900">{selectedEmployee.title || '-'}</div>
              </div>
              <div>
                <span className="text-xs text-gray-500">{t('Department')}</span>
                <div className="text-sm font-medium text-gray-900">{selectedEmployee.department || '-'}</div>
              </div>
            </div>
          )}

          {/* Manager Selection */}
          {noSupervisor && formData.userId && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-md text-sm flex items-center gap-2">
              <Info className="w-4 h-4 flex-shrink-0" />
              {t('This employee has no assigned supervisor. Showing all employees instead.')}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Manager')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.managerId}
              onChange={handleManagerSelect}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Select manager...')}</option>
              {managers.map(mgr => (
                <option key={mgr.id} value={mgr.id}>
                  {mgr.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Offboarding Template */}
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Offboarding Template')} <span className="text-xs font-normal text-gray-400">({t('optional')})</span>
              </label>
              <select
                value={formData.templateId}
                onChange={(e) => setFormData(prev => ({ ...prev, templateId: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('No template')}</option>
                {templates.map(template => (
                  <option key={template.id} value={String(template.id)}>
                    {template.name} {template.isDefault && t('(Default)')}
                  </option>
                ))}
              </select>
              {formData.templateId ? (
                <p className="text-xs text-gray-500 mt-1">
                  {templates.find(t => t.id === parseInt(formData.templateId))?.description}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {t('Leave empty to terminate immediately without a checklist.')}
                </p>
              )}
            </div>
          )}

          {/* Last Working Day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Last Working Day')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.lastWorkingDay}
              onChange={(e) => setFormData(prev => ({ ...prev, lastWorkingDay: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Exit Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Exit Reason')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.exitReasonId}
              onChange={(e) => setFormData(prev => ({ ...prev, exitReasonId: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Select exit reason...')}</option>
              {exitReasons.map(reason => (
                <option key={reason.id} value={reason.id}>{t(reason.name)}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={!formData.userId || !formData.lastWorkingDay || !formData.exitReasonId || !formData.managerId || submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('Starting...') : t('Start Offboarding')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartOffboardingModal;
